import { z } from 'zod';

// ============================================================================
// Task Fase 6 (typing FE) — DTO risposta condivisi per gli endpoint del Site
// Builder / Landing Pages consumati da
// packages/frontend/src/components/ai/LandingPageBuilder.tsx.
//
// Shape OSSERVATE dal backend reale in
// packages/backend/src/routes/landing/landing-pages.routes.ts e dai servizi
// landing-page-templates.ts / landing-component-library.ts:
//   - GET /landing-pages                        → list (SELECT colonne note)
//   - GET /landing-pages/:id                     → SELECT * riga completa
//   - GET /landing-pages/site-groups             → [{ id, name }]
//   - POST /landing-pages/site-groups            → { id, name }
//   - GET /landing-pages/available-widgets       → { forms, chatbots, ... }
//   - GET /landing-pages/:id/versions            → [{ id, version_number, created_at }]
//   - POST /landing-pages/:id/versions/:v/restore→ riga tenant_landing_pages
//   - POST /landing-pages/upload-image           → { url, filename, ... }
//   - POST /landing-pages/ai/scrape              → { title, content, ... }
//   - POST /landing-pages/ai/chat                → { message, sections, ... }
//   - GET /landing-pages/component-library       → { categories: [...] }
//   - GET /landing-pages/templates|child-templates → [ template ]
//   - GET /landing-pages/font-pairs              → [{ id, name, displayFont, bodyFont }]
//   - GET /landing-pages/:id|project/:g/analytics→ aggregati (loose)
//   - GET /landing-pages/site-group/:g/snippets  → { custom_head_code, custom_body_code }
//
// Convenzioni wire (come schemas/call-routing.ts):
//   - colonne NULLABLE / alias LEFT JOIN → .nullable().optional()
//   - SELECT * / jsonb liberi → campi osservati + .passthrough()
//   - timestamp → z.union([z.string(), z.date()])
//
// NB: il modello del site-builder (sections / props / theme / settings) è
// GENUINAMENTE DINAMICO (jsonb salvato dall'editor + generato dall'AI): NON si
// inventano contratti stretti. Si usano tipi LARGHI documentati (record con
// chiavi note opzionali + passthrough), MAI `any`.
// ============================================================================

const wireTimestamp = z.union([z.string(), z.date()]);

// ----------------------------------------------------------------------------
// Sezione della landing page. Modello DINAMICO: `type` guida il rendering e
// `props` è un blob jsonb libero (varia per ogni tipo di sezione e per i props
// generati dall'AI). Non esiste un contratto stretto per `props`: usiamo un
// record largo con le sole chiavi strutturali note (id/type/props) +
// passthrough per tollerare qualsiasi campo aggiuntivo.
// ----------------------------------------------------------------------------
export const landingSectionSchema = z
  .object({
    id: z.string().optional(),
    type: z.string().optional(),
    props: z.record(z.unknown()).optional(),
  })
  .passthrough();
export type LandingSection = z.infer<typeof landingSectionSchema>;

// ----------------------------------------------------------------------------
// seo_meta e settings sono jsonb liberi persistiti dall'editor. Chiavi note
// opzionali (usate dalla UI) + passthrough per il resto (dinamico).
// ----------------------------------------------------------------------------
export const landingSeoMetaSchema = z
  .object({
    title: z.string().optional(),
    description: z.string().optional(),
    ogImage: z.string().optional(),
  })
  .passthrough();
export type LandingSeoMeta = z.infer<typeof landingSeoMetaSchema>;

export const landingSettingsSchema = z
  .object({
    primaryColor: z.string().optional(),
    accentColor: z.string().optional(),
    custom_head_code: z.string().optional(),
    custom_body_code: z.string().optional(),
    custom_css: z.string().optional(),
    temperature: z.number().optional(),
    fontPair: z.string().optional(),
    businessType: z.string().optional(),
  })
  .passthrough();
export type LandingSettings = z.infer<typeof landingSettingsSchema>;

// ----------------------------------------------------------------------------
// Analytics riepilogo per la lista (getter `enriched` in GET /landing-pages).
// ----------------------------------------------------------------------------
export const landingPageListAnalyticsSchema = z.object({
  total_views: z.number(),
  views_7d: z.number(),
  sparkline: z.array(z.number()),
});
export type LandingPageListAnalytics = z.infer<typeof landingPageListAnalyticsSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages → data: LandingPageDto[]. La SELECT lista tipizza le
// colonne note; `sections` NON è nella SELECT lista (solo nel dettaglio) ma è
// modellato optional per condividere il DTO col dettaglio SELECT *.
// GET /landing-pages/:id → data: SELECT * (riga completa, stessi campi +
//   eventuali colonne aggiuntive → passthrough). `sections`/`seo_meta`/
//   `settings` sul wire possono arrivare come stringa jsonb non ancora
//   parsata: il DTO li accetta come oggetto O stringa (union larga).
// ----------------------------------------------------------------------------
const jsonbOrString = <T extends z.ZodTypeAny>(inner: T) =>
  z.union([inner, z.string()]);

export const landingPageDtoSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    description: z.string().nullable().optional(),
    status: z.string(), // 'draft' | 'published' | 'archived'
    sections: jsonbOrString(z.array(landingSectionSchema)).nullable().optional(),
    seo_meta: jsonbOrString(landingSeoMetaSchema).nullable().optional(),
    settings: jsonbOrString(landingSettingsSchema).nullable().optional(),
    site_group_id: z.string().nullable().optional(),
    parent_id: z.string().nullable().optional(),
    tenant_id: z.string().optional(),
    created_at: wireTimestamp.optional(),
    updated_at: wireTimestamp.optional(),
    published_at: wireTimestamp.nullable().optional(),
    // Arricchimento della lista (assente nel dettaglio).
    analytics: landingPageListAnalyticsSchema.optional(),
  })
  .passthrough();
export type LandingPageDto = z.infer<typeof landingPageDtoSchema>;

// ----------------------------------------------------------------------------
// GET/POST /landing-pages/site-groups
// ----------------------------------------------------------------------------
export const landingSiteGroupDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .passthrough();
export type LandingSiteGroupDto = z.infer<typeof landingSiteGroupDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/linkable-pages → data: righe con id/slug/title/status/
// site_group_id/parent_id/sections. La UI arricchisce con sectionAnchors lato
// client (campo opzionale).
// ----------------------------------------------------------------------------
export const landingLinkablePageDtoSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    status: z.string(),
    site_group_id: z.string().nullable().optional(),
    parent_id: z.string().nullable().optional(),
    sections: jsonbOrString(z.array(landingSectionSchema)).nullable().optional(),
    sectionAnchors: z
      .array(z.object({ id: z.string(), type: z.string(), label: z.string() }).passthrough())
      .optional(),
  })
  .passthrough();
export type LandingLinkablePageDto = z.infer<typeof landingLinkablePageDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/available-widgets → data: { forms, chatbots, voiceAgents,
// geminiVoiceAgents, taskForms, appointmentForms }. Ogni voce è una riga
// SELECT con colonne note (id/name/...) + passthrough (jsonb / colonne extra).
// ----------------------------------------------------------------------------
export const landingWidgetOptionSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
    slug: z.string().nullable().optional(),
    is_active: z.boolean().nullable().optional(),
    board_name: z.string().nullable().optional(), // taskForms (LEFT JOIN task_boards)
    auto_confirm: z.boolean().nullable().optional(), // appointmentForms
    elevenlabs_agent_id: z.string().nullable().optional(), // voiceAgents
    voice: z.string().nullable().optional(), // geminiVoiceAgents
    language: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
  })
  .passthrough();
export type LandingWidgetOption = z.infer<typeof landingWidgetOptionSchema>;

export const landingAvailableWidgetsDtoSchema = z.object({
  forms: z.array(landingWidgetOptionSchema),
  chatbots: z.array(landingWidgetOptionSchema),
  voiceAgents: z.array(landingWidgetOptionSchema),
  geminiVoiceAgents: z.array(landingWidgetOptionSchema),
  taskForms: z.array(landingWidgetOptionSchema),
  appointmentForms: z.array(landingWidgetOptionSchema),
});
export type LandingAvailableWidgetsDto = z.infer<typeof landingAvailableWidgetsDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/:id/versions → data: [{ id, version_number, created_at }]
// POST .../versions/:versionId/restore → data: riga tenant_landing_pages
// aggiornata (sections/settings possono arrivare come stringa jsonb).
// ----------------------------------------------------------------------------
export const landingPageVersionDtoSchema = z
  .object({
    id: z.string(),
    version_number: z.number(),
    created_at: wireTimestamp,
    sections: jsonbOrString(z.array(landingSectionSchema)).nullable().optional(),
    settings: jsonbOrString(landingSettingsSchema).nullable().optional(),
  })
  .passthrough();
export type LandingPageVersionDto = z.infer<typeof landingPageVersionDtoSchema>;

// ----------------------------------------------------------------------------
// POST /landing-pages/upload-image → data: { url, filename, originalName,
// size, mimetype }.
// ----------------------------------------------------------------------------
export const landingUploadImageDtoSchema = z
  .object({
    url: z.string(),
    filename: z.string().optional(),
    originalName: z.string().nullable().optional(),
    size: z.number().nullable().optional(),
    mimetype: z.string().nullable().optional(),
  })
  .passthrough();
export type LandingUploadImageDto = z.infer<typeof landingUploadImageDtoSchema>;

// ----------------------------------------------------------------------------
// POST /landing-pages/ai/scrape → data: { title, content, description, url,
// images: [{ url, alt? }] }.
// ----------------------------------------------------------------------------
export const landingScrapeImageSchema = z
  .object({
    url: z.string(),
    alt: z.string().optional(),
  })
  .passthrough();

export const landingScrapeDtoSchema = z
  .object({
    title: z.string().optional(),
    content: z.string(),
    description: z.string().optional(),
    url: z.string().optional(),
    images: z.array(landingScrapeImageSchema).optional(),
  })
  .passthrough();
export type LandingScrapeDto = z.infer<typeof landingScrapeDtoSchema>;

// ----------------------------------------------------------------------------
// POST /landing-pages/ai/chat → data: { message, sections, phase,
// imagesSummary, imageGenResults, usage, modelUsed, modelRequested, fallback }.
// `sections` è il modello dinamico (LandingSection[]). `usage` è il blob usage
// del provider AI → record largo.
// ----------------------------------------------------------------------------
export const landingAiChatDtoSchema = z
  .object({
    message: z.string(),
    sections: z.array(landingSectionSchema).nullable().optional(),
    phase: z.string().optional(),
    imagesSummary: z.array(z.string()).optional(),
    imageGenResults: z
      .object({ generated: z.number(), failed: z.number() })
      .passthrough()
      .optional(),
    usage: z.record(z.unknown()).nullable().optional(),
    modelUsed: z.string().optional(),
    modelRequested: z.string().optional(),
    fallback: z.boolean().optional(),
  })
  .passthrough();
export type LandingAiChatDto = z.infer<typeof landingAiChatDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/component-library → data: { categories: [...] }.
// Ogni categoria contiene varianti con `defaultProps` (jsonb dinamico).
// ----------------------------------------------------------------------------
export const landingComponentVariantSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    sectionType: z.string(),
    defaultProps: z.record(z.unknown()).optional(),
    preview: z.string().optional(),
  })
  .passthrough();
export type LandingComponentVariant = z.infer<typeof landingComponentVariantSchema>;

export const landingComponentCategorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    icon: z.string().optional(),
    components: z.array(landingComponentVariantSchema),
  })
  .passthrough();
export type LandingComponentCategory = z.infer<typeof landingComponentCategorySchema>;

export const landingComponentLibraryDtoSchema = z.object({
  categories: z.array(landingComponentCategorySchema),
});
export type LandingComponentLibraryDto = z.infer<typeof landingComponentLibraryDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/templates | /child-templates → data: [ template ].
// GET /landing-pages/templates/:id → data: singolo template (con sections +
// settings, entrambi jsonb dinamici).
// ----------------------------------------------------------------------------
export const landingTemplateDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    icon: z.string().optional(),
    color: z.string().optional(),
    sections: z.array(landingSectionSchema).optional(),
    settings: landingSettingsSchema.optional(),
  })
  .passthrough();
export type LandingTemplateDto = z.infer<typeof landingTemplateDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/font-pairs → data: [{ id, name, displayFont, bodyFont }].
// ----------------------------------------------------------------------------
export const landingFontPairDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    displayFont: z.string(),
    bodyFont: z.string(),
  })
  .passthrough();
export type LandingFontPairDto = z.infer<typeof landingFontPairDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/site-group/:groupId/snippets → { custom_head_code,
// custom_body_code } (NB: risposta "nuda", NON wrappata in { success, data }).
// ----------------------------------------------------------------------------
export const landingGroupSnippetsDtoSchema = z
  .object({
    custom_head_code: z.string().nullable().optional(),
    custom_body_code: z.string().nullable().optional(),
  })
  .passthrough();
export type LandingGroupSnippetsDto = z.infer<typeof landingGroupSnippetsDtoSchema>;

// ----------------------------------------------------------------------------
// GET /landing-pages/:id/analytics e /project/:g/analytics → aggregati
// eterogenei (daily/totals/last7/topReferrers/devices/eventCounts/webVitals/
// heatmapData/...). Il payload è ampio e variabile (branch tabelle mancanti →
// emptyResult); il FE lo passa opaco a LandingAnalyticsDashboard. Modello
// LARGO documentato: chiavi note opzionali + passthrough, NESSUN contratto
// stretto (i singoli aggregati sono array di righe SQL dinamiche).
// ----------------------------------------------------------------------------
export const landingAnalyticsDtoSchema = z
  .object({
    daily: z.array(z.record(z.unknown())).optional(),
    totals: z.record(z.unknown()).nullable().optional(),
    last7: z.record(z.unknown()).nullable().optional(),
    topReferrers: z.array(z.record(z.unknown())).optional(),
    utmBreakdown: z.array(z.record(z.unknown())).optional(),
    devices: z.array(z.record(z.unknown())).optional(),
    pageBreakdown: z.array(z.record(z.unknown())).optional(),
    // Righe SQL: [{ event_type, count, ... }] (backend LpEventCount[]). Il FE
    // fa .find/.map: è un ARRAY sul wire, non un record.
    eventCounts: z.array(z.record(z.unknown())).optional(),
    avgTimeOnPage: z.number().nullable().optional(),
    bounceRate: z.number().nullable().optional(),
    scrollDepthDist: z.array(z.record(z.unknown())).optional(),
    webVitals: z.record(z.unknown()).nullable().optional(),
    pages: z.array(z.record(z.unknown())).optional(),
    heatmapData: z.array(z.record(z.unknown())).optional(),
    ctaConversionRate: z.number().nullable().optional(),
    clickData: z.array(z.record(z.unknown())).optional(),
    ga4Active: z.boolean().optional(),
    currentWeekConversions: z.record(z.unknown()).nullable().optional(),
    prevWeekConversions: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();
export type LandingAnalyticsDto = z.infer<typeof landingAnalyticsDtoSchema>;

// ----------------------------------------------------------------------------
// GET /ai-tools/service-models → data: [{ service_key, provider, current_model }]
// Consumato dai selettori del Site Builder per idratare i modelli globali.
// ----------------------------------------------------------------------------
export const landingServiceModelEntryDtoSchema = z
  .object({
    service_key: z.string(),
    provider: z.string(),
    current_model: z.string(),
  })
  .passthrough();
export type LandingServiceModelEntryDto = z.infer<typeof landingServiceModelEntryDtoSchema>;
